import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { activeNetworkSelector } from '../../../redux/selectors';

const createSkeletonRow = (index) => ({
  index,
  original: {
    id: `skeleton-${index}`,
    index,
    isSkeleton: true,
  },
  options: {
    showLoader: true,
  },
  rowId: `skeleton-${index}`,
  id: `skeleton-${index}`,
  contact: {
    id: `skeleton-${index}`,
    fullName: '',
    profilePicUrl: '',
    importers: [],
    isSkeleton: true,
  },
  organization: {
    id: `skeleton-${index}`,
    name: '',
    logoUrl: '',
    isSkeleton: true,
  },
  jobTitle: '',
  teamInvolvement: '',
  hiredDate: '',
  source: [],
  contactOf: [],
  isSkeleton: true,
});

export const useHiresTableRows = (data, showSkeleton = false) => {
  const activeNetwork = useSelector(activeNetworkSelector);
  const history = useHistory();

  return useMemo(() => {
    if (showSkeleton) {
      // Return skeleton rows when loading
      return Array.from({ length: 10 }, (_, index) => createSkeletonRow(index));
    }

    return (
      data?.map((hire, index) => {
        const contactId = hire.contact?.id || hire.contactId;
        const contactUrl = `/networks/${activeNetwork?.slug}/hires/${contactId}${history.location.search}`;
        // After deserialization, contact should be flattened, but handle both cases
        const contact = hire.contact?.attributes
          ? {
              id: hire.contact.id,
              fullName: hire.contact.attributes.fullName || hire.contact.attributes.full_name,
              profilePicUrl: hire.contact.attributes.profilePicUrl || hire.contact.attributes.profile_pic_url,
              importers: hire.contact.relationships?.importers?.data || hire.contact.importers || [],
            }
          : hire.contact || {};

        // After deserialization, organization should be flattened, but handle both cases
        let organization = {};
        if (hire.organization?.data?.[0]?.attributes) {
          organization = {
            id: hire.organization.data[0].id,
            name: hire.organization.data[0].attributes.name,
            logoUrl: hire.organization.data[0].attributes.logoUrl || hire.organization.data[0].attributes.logo_url,
          };
        } else if (hire.organization?.attributes) {
          organization = {
            id: hire.organization.id,
            name: hire.organization.attributes.name,
            logoUrl: hire.organization.attributes.logoUrl || hire.organization.attributes.logo_url,
          };
        } else if (hire.organization) {
          // eslint-disable-next-line prefer-destructuring
          organization = hire.organization;
        }

        // Extract importers from contact (could be in relationships or direct)
        const importers =
          contact.relationships?.importers?.data ||
          contact.importers ||
          hire.contact?.relationships?.importers?.data ||
          hire.contact?.importers ||
          [];

        return {
          index,
          original: {
            ...hire,
            index,
            contact: {
              ...contact,
              importers,
            },
            organization,
          },
          options: {
            showLoader: false,
            contactUrl,
            type: 'private',
            hideEdit: true,
          },
          rowId: hire.id,
          id: hire.id,
          contact: {
            ...contact,
            importers,
          },
          organization,
          jobTitle: hire.jobTitle || '',
          teamInvolvement: hire.teamInvolvement || 'unknown',
          hiredDate: hire.dateFrom || '',
          source: contact.sources,
          contactOf: importers,
        };
      }) ?? []
    );
  }, [data, showSkeleton, activeNetwork?.slug, history.location.search]);
};
